// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson <hi@ben.gy>
// Additional terms under AGPL section 7 apply — see ADDITIONAL-TERMS.md.

/**
 * Before/after compare slider. Both images are shown at the same on-screen
 * size, so the "before" (original, upscaled by the browser with bicubic) looks
 * soft next to the model's crisp "after". A draggable divider — pointer or
 * keyboard — clips between them. Uses `clip-path: inset()` so nothing squishes.
 */

export interface CompareHandle {
  destroy(): void;
}

export function mountCompare(
  container: HTMLElement,
  beforeUrl: string,
  afterUrl: string,
  aspect: number,
): CompareHandle {
  container.innerHTML = '';
  const frame = document.createElement('div');
  frame.className = 'compare-frame';
  frame.style.aspectRatio = String(aspect || 1);

  const before = document.createElement('img');
  before.className = 'compare-img compare-before';
  before.src = beforeUrl;
  before.alt = 'Original image, enlarged by the browser';
  before.draggable = false;

  const after = document.createElement('img');
  after.className = 'compare-img compare-after';
  after.src = afterUrl;
  after.alt = 'Upscaled result';
  after.draggable = false;

  const labelBefore = document.createElement('span');
  labelBefore.className = 'compare-label compare-label-before';
  labelBefore.textContent = 'Before';
  const labelAfter = document.createElement('span');
  labelAfter.className = 'compare-label compare-label-after';
  labelAfter.textContent = 'After';

  const divider = document.createElement('div');
  divider.className = 'compare-divider';
  divider.setAttribute('role', 'slider');
  divider.setAttribute('tabindex', '0');
  divider.setAttribute('aria-label', 'Reveal upscaled result');
  divider.setAttribute('aria-valuemin', '0');
  divider.setAttribute('aria-valuemax', '100');
  const knob = document.createElement('div');
  knob.className = 'compare-knob';
  knob.innerHTML =
    '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="m9 7-5 5 5 5M15 7l5 5-5 5"/></svg>';
  divider.appendChild(knob);

  frame.append(before, after, labelBefore, labelAfter, divider);
  container.appendChild(frame);

  let pct = 55;
  const apply = () => {
    // Show the "after" from the divider rightwards.
    after.style.clipPath = `inset(0 0 0 ${pct}%)`;
    divider.style.left = `${pct}%`;
    divider.setAttribute('aria-valuenow', String(Math.round(pct)));
  };
  apply();

  const setFromClientX = (clientX: number) => {
    const rect = frame.getBoundingClientRect();
    if (rect.width === 0) return;
    pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    apply();
  };

  let dragging = false;
  const onDown = (e: PointerEvent) => {
    dragging = true;
    divider.setPointerCapture?.(e.pointerId);
    setFromClientX(e.clientX);
    e.preventDefault();
  };
  const onMove = (e: PointerEvent) => {
    if (!dragging) return;
    setFromClientX(e.clientX);
  };
  const onUp = (e: PointerEvent) => {
    dragging = false;
    divider.releasePointerCapture?.(e.pointerId);
  };
  // Allow grabbing anywhere on the frame, not just the thin divider.
  frame.addEventListener('pointerdown', onDown);
  frame.addEventListener('pointermove', onMove);
  frame.addEventListener('pointerup', onUp);
  frame.addEventListener('pointercancel', onUp);

  const onKey = (e: KeyboardEvent) => {
    if (e.key === 'ArrowLeft') {
      pct = Math.max(0, pct - 4);
      apply();
      e.preventDefault();
    } else if (e.key === 'ArrowRight') {
      pct = Math.min(100, pct + 4);
      apply();
      e.preventDefault();
    }
  };
  divider.addEventListener('keydown', onKey);

  return {
    destroy() {
      frame.removeEventListener('pointerdown', onDown);
      frame.removeEventListener('pointermove', onMove);
      frame.removeEventListener('pointerup', onUp);
      frame.removeEventListener('pointercancel', onUp);
      divider.removeEventListener('keydown', onKey);
      container.innerHTML = '';
    },
  };
}
